import type { StoreActionApi } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	ExperienceName,
	type ExperienceNameType,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	isAUPViolationError,
	isChannelNotFoundError,
	isHipaaError,
	isRateLimitedError,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	isStoreError,
	type ChatStorePrivateState,
	type Conversation,
	type StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { createStoreError } from './createStoreError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { isEmptyConversation } from './isEmptyConversation';
import type {
	ActionErrorHandler,
	ChatStoreAction,
	ChatStoreContainerProps,
	ErrorReason,
} from './types';

type GetErrorReasonParams = {
	error: unknown;
	storeError: StoreError;
	currentConversationId: string;
	conversations: ChatStorePrivateState['conversations'];
};

const getErrorReason = ({
	error,
	storeError,
	currentConversationId,
	conversations,
}: GetErrorReasonParams): ErrorReason => {
	if (isHipaaError(error)) {
		return 'hipaa';
	}
	if (isAUPViolationError(error)) {
		return 'aupViolation';
	}
	if (isChannelNotFoundError(error)) {
		return 'channelNotFound';
	}
	if (!conversations?.[currentConversationId] || isEmptyConversation(currentConversationId)) {
		return 'noActiveConversation';
	}
	if (storeError.status_code === 429 || isRateLimitedError(error)) {
		return 'rateLimited';
	}
	return 'generic';
};

export type StoreErrorHandlerProps<Args> = {
	args: Args;
	api: StoreActionApi<ChatStorePrivateState>;
	actionLoggableName: StoreActionLoggableName;
	containerProps: ChatStoreContainerProps;
	error: unknown;
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	experienceName?: (args: Args) => ExperienceNameType;
	actionErrorHandler?: ActionErrorHandler<Args>;
	getRateLimitState?: (args: Args) => { isDeepResearchAttempt?: boolean };
};

const reportChatError = (
	sendAnalyticsEvent: SendAnalyticsEvent,
	reason: ErrorReason,
	storeError: StoreError,
) => {
	if (!fg('ai_mate_unhandled_error_reporting')) {
		return;
	}

	sendAnalyticsEvent({
		eventType: 'operational',
		action: 'unhandledError',
		actionSubject: 'aiMate',
		attributes: {
			reason,
			statusCode: storeError.status_code,
			messageTemplate: storeError.message_template,
			errorType: storeError.errorType,
			traceId: storeError.traceId,
			errorContent: storeError.content,
			...(storeError.storeActionExecutionError
				? {
						actionLoggableName: storeError.storeActionExecutionError.actionLoggableName,
					}
				: {}),
		},
	});
};

/**
 * Centralized error handler for chat store actions.
 *
 * @typeParam Args - The type of the action arguments
 *
 * @param props - Configuration for error handling, including the original action args,
 *   store API, and optional custom error handler via `actionErrorHandler`
 *
 * @remarks
 * Implements a three-phase error handling pipeline:
 * 1. **Mandatory checks** - Critical errors that cannot be overridden (e.g., compliance violations)
 * 2. **Action-specific handling** - Custom error handling via `actionErrorHandler` prop
 * 3. **Default handlers** - Standard error handling based on categorized error reasons
 */
export const storeErrorHandler = <Args>(props: StoreErrorHandlerProps<Args>): void => {
	const {
		args,
		api,
		actionLoggableName,
		error,
		containerProps,
		actionErrorHandler,
		setConversationData,
	} = props;
	const { getState, setState, dispatch } = api;
	addSentryBreadcrumb(`createSafeAction: ${actionLoggableName} errored`);

	const storeError = createStoreError(error, undefined, actionLoggableName);
	const errorReason = getErrorReason({
		error,
		storeError,
		currentConversationId: getState().currentConversationId,
		conversations: getState().conversations,
	});

	// ============================================
	// PHASE 1: Mandatory checks (cannot be overridden)
	// ============================================

	// Handle experience tracking
	// Rate limits are ignored because they trigger a retry, not an experience termination
	if (props?.experienceName && errorReason !== 'rateLimited') {
		if (errorReason === 'aupViolation') {
			const aupError = error as {
				detail: { message: { message_template: string } };
				errorAttributes: Record<string, unknown>;
			};
			containerProps.experienceTracker.abort({
				name: ExperienceName.AIFC_MESSAGE,
				reason: aupError.detail.message.message_template,
				attributes: {
					...aupError.errorAttributes,
					errorType: error instanceof StreamErrorException ? 'StreamErrorException' : '',
				},
			});
		} else {
			containerProps.experienceTracker.fail({
				name: props.experienceName(args),
				error: error instanceof Error ? error : new Error(String(error)),
			});
		}
	}

	// Handle restricted use errors
	if (errorReason === 'hipaa') {
		containerProps.setConversationError({
			type: 'hipaa',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'hipaa', storeError);
		return;
	}

	// ============================================
	// PHASE 2: Action-specific error handling (optional)
	// ============================================

	if (actionErrorHandler) {
		const result = actionErrorHandler({
			args,
			error,
			storeError,
			containerProps,
			errorReason,
		});
		if (!result?.proceedToDefaultHandling) {
			return;
		}
	}

	// ============================================
	// PHASE 3: Default error handlers
	// ============================================

	if (errorReason === 'channelNotFound') {
		containerProps.setConversationError({
			type: 'conversationNotFound',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'channelNotFound', storeError);
		return;
	}

	if (errorReason === 'noActiveConversation') {
		containerProps.setConversationError({
			type: 'conversationEmptyOrNotPresentInStore',
			underlyingError: storeError,
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'noActiveConversation', storeError);
		if (storeError.message_template === ASSISTANCE_SERVICE_ERROR_CODES.ROVO_PRODUCT_NOT_ACTIVE) {
			containerProps.setRovoEnabled(false, storeError.message_template);
		}
		return;
	}

	const currentConversationId = getState().currentConversationId;

	if (errorReason === 'rateLimited') {
		const rateLimitState = props?.getRateLimitState?.(args) ?? {};
		setState({
			conversations: {
				...getState().conversations,
				[currentConversationId]: {
					...getState().conversations[currentConversationId],
					...clearStatus({ lastAgentMessageStatus: 'FAILED' }),
				},
			},
		});
		containerProps.setConversationError({
			type: 'rateLimited',
			underlyingError: storeError,
			rateLimitedActionType: rateLimitState.isDeepResearchAttempt ? 'deep-research' : 'generic',
		});
		reportChatError(containerProps.sendAnalyticsEvent, 'rateLimited', storeError);
		return;
	}

	dispatch(
		setConversationData({
			conversationId: currentConversationId,
			data: {
				...clearStatus({ lastAgentMessageStatus: 'FAILED' }),
			},
		}),
	);

	const currentMessages = containerProps.getMessages(currentConversationId);
	const lastMessage = currentMessages[currentMessages.length - 1];
	const isDuplicateError =
		lastMessage && isStoreError(lastMessage) && lastMessage.content === storeError.content;

	if (!isDuplicateError) {
		containerProps.setMessages(currentConversationId, [...currentMessages, storeError]);
	}

	const errorAttributes = (error as { errorAttributes?: Record<string, unknown> } | undefined)
		?.errorAttributes;

	containerProps.experienceTracker.fail({
		name: ExperienceName.AIFC_MESSAGE,
		error: error instanceof Error ? error : new Error(String(error)),
		attributes: {
			latestStreamMessageType: errorAttributes?.latestStreamMessageType,
			latestStreamMessageMillisOffset: errorAttributes?.latestStreamMessageMillisOffset,
		},
	});

	reportChatError(containerProps.sendAnalyticsEvent, 'generic', storeError);
};
